import { z } from 'zod';
import { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import routes from 'utils/routes';
import Form from 'components/form';
import SEO from 'components/SEO';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import TermsAndPolicy from 'components/TermsAndPolicy';
import { logger } from '../../utils/logger';

const ResetPassword = () => {
  const {
    actions: { resetPassword },
  } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('reset-token');
  const navigate = useNavigate();
  const [checkedPassword, setCheckedPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);

  useEffect(() => {
    if (!token) {
      // Log missing reset token for security monitoring
      logger.authWarn('Password reset attempted without token', {
        component: 'ResetPasswordPage'
      });
      
      navigate(routes.signInPath.href);
    }
  }, [token, navigate]);

  const formSchema = z
    .object({
      password: z.string(),
      confirm_password: z.string(),
      valid_password: z.boolean(),
    })
    .refine((data: { password: string; confirm_password: string }) => data.password === data.confirm_password, {
      message: "The passwords don't match",
      path: ['confirm_password'],
    });

  const { form, loading, error, isValid, setError } = useFormHandler(formSchema, {
    initialValues: {
      password: '',
      confirm_password: '',
      valid_password: false,
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await resetPassword(values.password, token || '');
      
      // Log successful password reset
      logger.info('Password reset completed successfully');
      
      navigate(routes.dashboardPath.href);
    } catch (error: any) {
      // Log password reset failures with context
      if (error?.response?.status === 400) {
        logger.authWarn('Password reset failed - invalid or expired token', {
          component: 'ResetPasswordPage'
        });
      } else {
        logger.authError('Password reset failed - system error', error as Error, {
          component: 'ResetPasswordPage'
        });
      }
      
      setError('An error occurred. Please try again.');
    }
  };

  const getConfirmPasswordError = () => {
    if (!form.values.confirm_password) {
      return undefined;
    }

    return form.values.confirm_password !== form.values.password
      ? 'Passwords do not match'
      : undefined;
  };

  return (
    <>
      <SEO 
        title="Reset Your Password - Secure Account Access"
        description="Create a new secure password for your Nucleus AI account. Reset your password to regain access to your AI receptionist dashboard."
        keywords="password reset, account security, AI receptionist login, secure access"
      />
    <div className="flex flex-col min-h-screen">
      <nav className="h-[70px] w-full px-4 md:px-6 flex items-center justify-between">
        <a
          href={routes.dashboardPath.href}
          className="flex items-center gap-4"
          tabIndex={6}
        >
          <img src="/images/nucleus-logo.svg" alt="Nucleus Logo" className="h-8" />
        </a>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 md:px-0">
        <div className="mx-auto">
          <div className="text-center mb-3 max-w-[600px] mx-auto">
            <h1 className="text-xl md:text-2xl font-semibold text-[#5E6AB2] mb-1">
              Reset Your Password - Secure Account Access
            </h1>
            <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4 text-center">
              Choose your new password
            </h2>
          </div>
          <div className="border custom-border-color bg-white rounded-xl p-4 md:p-6 md:w-5/6 xl:w-[440px] mx-auto">
            <Form form={form} error={error} onSubmit={onSubmit}>
              <Form.Password
                name="password"
                label="Password"
                placeholder="Password"
                tabIndex={1}
                onUpdate={(value: string) => setCheckedPassword(value)}
              />
              <Form.Password
                name="confirm_password"
                label="Confirm Password"
                placeholder="Confirm password"
                tabIndex={2}
                error={getConfirmPasswordError()}
              />
              <Form.PasswordCheck
                value={checkedPassword}
                isValid={passwordValid}
                validatePassword={(isValid: boolean) => {
                  setPasswordValid(isValid);
                  form.setValues({ valid_password: isValid });
                }}
              />
              <Form.Submit
                loading={loading}
                tabIndex={3}
                disabled={!isValid || loading || !passwordValid}
              >
                Reset Password
              </Form.Submit>
            </Form>
          </div>
        </div>
      </div>
      <div className="h-[50px]">
        <TermsAndPolicy termsTabIndex={4} privacyTabIndex={5} />
      </div>
    </div>
    </>
  );
};

export default ResetPassword;
